import React, { useState } from 'react';
import { Button } from '@/components/ui';

interface ReferenceSearchResult {
  chunk: {
    chunkId: string;
    documentId: string;
    documentTitle: string;
    text: string;
    sectionTitle?: string;
    pageNumber?: number;
  };
  score: number;
  document: {
    documentId: string;
    title: string;
    documentType: string;
    standardNumber?: string;
    revisionDate?: string;
  };
}

interface ReferencedDocumentsPanelProps {
  searchResults?: {
    results: ReferenceSearchResult[];
    query: string;
  };
}

export default function ReferencedDocumentsPanel({ searchResults }: ReferencedDocumentsPanelProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!searchResults || !searchResults.results || searchResults.results.length === 0) {
    return null;
  }

  const { results, query } = searchResults;

  return (
    <div className="bg-secondary border border-border rounded-lg overflow-hidden">
      {/* Header */}
      <Button
        variant="ghost"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-secondary transition-colors h-auto"
      >
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
          <span className="font-medium text-muted-foreground">
            AI Referenced {results.length} Standard{results.length !== 1 ? 's' : ''}
          </span>
          <span className="text-sm text-muted-foreground">
            (Query: "{query.length > 40 ? query.substring(0, 40) + '...' : query}")
          </span>
        </div>
        <svg
          className={`w-5 h-5 text-muted-foreground transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </Button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-border bg-card">
          <div className="divide-y divide-purple-100">
            {results.map((result) => (
              <div key={result.chunk.chunkId} className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-foreground">
                        {result.document.title}
                      </span>
                      {result.document.standardNumber && (
                        <span className="px-2 py-0.5 bg-secondary text-muted-foreground text-xs font-medium rounded">
                          {result.document.standardNumber}
                        </span>
                      )}
                      <span className="text-xs text-muted-foreground">
                        {(result.score * 100).toFixed(1)}% match
                      </span>
                    </div>
                    {result.chunk.sectionTitle && (
                      <div className="text-sm text-muted-foreground mb-2">
                        § {result.chunk.sectionTitle}
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-muted rounded p-3 text-sm text-foreground leading-relaxed">
                  {result.chunk.text.length > 300 ? (
                    <>
                      {result.chunk.text.substring(0, 300)}...
                      <Button variant="link" size="xs" className="text-muted-foreground hover:text-foreground ml-2">
                        Read more
                      </Button>
                    </>
                  ) : (
                    result.chunk.text
                  )}
                </div>

                {/* Citation Helper */}
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Suggested citation:</span>
                  <code className="text-xs bg-secondary text-muted-foreground px-2 py-1 rounded font-mono">
                    ({result.document.standardNumber || result.document.title} §{result.chunk.sectionTitle || 'Section'})
                  </code>
                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={() => {
                      const citation = `(${result.document.standardNumber || result.document.title} §${result.chunk.sectionTitle || 'Section'})`;
                      navigator.clipboard.writeText(citation);
                    }}
                    title="Copy citation"
                  >
                    📋 Copy
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Info Footer */}
      <div className="px-4 py-2 bg-secondary border-t border-border">
        <p className="text-xs text-muted-foreground">
          💡 The AI assistant consulted these standards when generating edits. Citations are included in the changes.
        </p>
      </div>
    </div>
  );
}

